import { Component, DestroyRef, Input, OnChanges, OnInit, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EdsPromoTokenType } from '@bpos';
import { SlipState } from '@bpos/v1/my-bets';
import { EpcotConfig, SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { hasValue } from '@frontend/sports/common/core/utils/extended-types';
import { CRMTokenImage } from '@frontend/sports/types/models/crm-tokens';
import { SyncUserSettingsService } from '@frontend/sports/user/feature';
import { BetslipIntegrationService } from 'packages/sports/common/betslip/integration/betslip-integration.service';
import { Observable, distinctUntilChanged } from 'rxjs';

import { ISignpostingAwardBanner, ITokenSignposting, PostbetSignpostingService } from '../../../crm-postbet-signposting/postbet-signposting.service';
import { MyBetsBetSlip, MyBetsListContext } from '../../../my-bets-base/models/my-bets-viewmodels';
import { MyBetsUtilService } from '../../services/my-bets-util.service';
import { PendingPayoutInfoService } from '../../services/pending-payout-info.service';
import { ReBetAvailabilityService } from '../re-bet/re-bet-visibility.service';
import { MyBetsToolbarComponent } from '../toolbar/my-bets-toolbar.component';

@Component({
    selector: 'ms-my-bets-betslip',
    templateUrl: 'my-bets-betslip.component.html',
    styleUrl: 'my-bets-betslip.component.scss',
    standalone: false,
})
export class MyBetsBetslipComponent implements OnInit, OnChanges {
    @Input() betslip: MyBetsBetSlip;
    @Input() myBetsListContext: MyBetsListContext;
    @Input() isLast: boolean;
    @ViewChild('toolbar') toolbar: MyBetsToolbarComponent;

    isBetInEditMode = false;
    showEmptySpace = false;
    automationEventIds = '';
    showBetAndGet = false;
    signpostingAwardBanners: ISignpostingAwardBanner[] = [];
    tokenSignPost: ITokenSignposting | undefined;
    enableFlash = false;
    pendingPayoutInfoVisible$: Observable<boolean>;
    enableRedesignedBetCard = this.myBetsUtilService.isBetCardRedesigned();
    isEpcotEnabled = this.epcotConfig.isEnabled;
    cashedOut = false;
    cancelledBet = false;
    rewardTokenImage = signal<CRMTokenImage | undefined>(undefined);
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private postbetSignpostingService: PostbetSignpostingService,
        private betslipIntegrationService: BetslipIntegrationService,
        private reBetAvailabilityService: ReBetAvailabilityService,
        private sitecoreMyBets: SitecoreMyBetsConfig,
        private pendingPayoutInfoService: PendingPayoutInfoService,
        public myBetsUtilService: MyBetsUtilService,
        private userSettings: SyncUserSettingsService,
        private epcotConfig: EpcotConfig,
    ) {}

    ngOnInit(): void {
        this.betslipIntegrationService
            .isEditBetActive$(this.betslip.betslipRealId)
            .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe((state) => {
                this.isBetInEditMode = state;
            });
    }

    autoCashoutPopperTriggered(action: 'open' | 'close'): void {
        this.showEmptySpace = this.isLast && action === 'open';
    }

    ngOnChanges(): void {
        this.cancelledBet = this.betslip.state === SlipState.Canceled;
        this.cashedOut = this.betslip.isCachedOut;
        this.signpostingAwardBanners = this.postbetSignpostingService.getAwardBanners(this.betslip.signPostingRewards);
        this.automationEventIds = (this.betslip.bets ?? [])
            .map((bet) => bet.event?.id)
            .filter(hasValue)
            .join(',');
        this.showBetAndGet = this.betslip.edsPromoTokens.length === 1 && this.betslip.edsPromoTokens[0].tokenType === EdsPromoTokenType.BetAndGet;
        this.tokenSignPost = this.postbetSignpostingService.getBetAndGetToken(this.betslip);
        if (this.enableRedesignedBetCard) {
            this.rewardTokenImage.set(this.postbetSignpostingService.getRewardTokenImage(this.betslip.usedRewardToken?.tokenType));
        }
        this.pendingPayoutInfoVisible$ = this.pendingPayoutInfoService.visible$(this.betslip.betslipRealId);
    }

    toggleToolbar(): void {
        this.toolbar.toggleToolbar();
    }

    get showReBet(): boolean {
        return this.reBetAvailabilityService.isAvailable(this.myBetsListContext.listType);
    }

    get getCashoutTaxMessage(): string {
        return this.sitecoreMyBets.myBetsTax.CashOutWinningTaxMessage;
    }

    get isCashoutWinningsTaxMessage(): boolean {
        if (!this.betslip || !this.betslip.earlyPayout) {
            return false;
        }

        return this.userSettings.isTurnoverTaxApplied && this.betslip.earlyPayout.earlyPayoutPossible;
    }

    setFlash(isFlashOn: boolean): void {
        this.enableFlash = isFlashOn;
    }
}
